import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { QuotationExceptionItem } from '@/types/domain/quotation-comparison';
import { AlertTriangle, ShieldAlert } from 'lucide-react';

interface QuotationExceptionCenterProps {
  exceptions: QuotationExceptionItem[];
}

export const QuotationExceptionCenter: React.FC<QuotationExceptionCenterProps> = ({ exceptions }) => {
  if (exceptions.length === 0) {
    return (
      <Card className="border-border rounded-2xl shadow-2xs font-poppins">
        <CardContent className="p-4 text-center text-xs text-emerald-700 font-bold">
          ✓ Zero Exception Flags Detected across all received quotation bids.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-border rounded-2xl shadow-2xs font-poppins">
      <CardHeader className="pb-3 border-b border-border/60">
        <CardTitle className="font-poppins text-xs font-bold text-foreground uppercase tracking-wider flex items-center justify-between">
          <span>Sourcing Exception & Anomaly Center</span>
          <span className="text-[10px] font-mono text-rose-600 font-bold">{exceptions.length} Anomaly Flags</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4 space-y-3">
        {exceptions.map((exc) => (
          <div
            key={exc.id}
            className={`p-3.5 rounded-xl border space-y-1.5 text-xs ${
              exc.severity === 'HIGH' || exc.severity === 'CRITICAL'
                ? 'border-rose-500/30 bg-rose-500/5 text-rose-900'
                : 'border-amber-500/30 bg-amber-500/5 text-amber-900'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 font-bold">
                {exc.severity === 'HIGH' ? (
                  <ShieldAlert className="h-4 w-4 text-rose-600" />
                ) : (
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                )}
                <span>{exc.vendorName}</span>
              </div>

              <span
                className={`text-[10px] font-extrabold uppercase px-2.5 py-0.5 rounded-full ${
                  exc.severity === 'HIGH' ? 'bg-rose-500/20 text-rose-700' : 'bg-amber-500/20 text-amber-800'
                }`}
              >
                {exc.category.replace(/_/g, ' ')} ({exc.severity})
              </span>
            </div>

            <p className="text-[11px] font-semibold text-foreground-secondary leading-relaxed">
              {exc.description}
            </p>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};
